import * as React from 'react'
import {
  Box, Table, TableHead, TableBody, TableRow, TableCell,
  Checkbox, Tooltip, Typography, Button, Pagination, TableSortLabel,
} from '@mui/material'
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {dataGridWrapSx, headerCellSx, cellSx, primaryBtnSx, secondaryBtnSx, alertStripSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, MUTED, PRIMARY_BLUE, INFO} from '../../../../styles/foundation/colors'
import {GAP} from '../../../../styles/foundation/spacing'
import {kg, int} from '../format'
import {packLabel} from '../weightMath'
import {sortRows, nextSort, modalFacts, pickedRows} from './rows'
import {weightFacts} from './request'
import IniSpark from '../components/IniSpark'
import EmptyState from '../components/EmptyState'

const PER_PAGE = 10

const subtleTextSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}

/**
 * ONE numeric cell style for the whole grid. `fontVariantNumeric` is set on the
 * CELL rather than on each Typography so a figure and the qualifier beneath it
 * are both tabular — otherwise the digits in the sub-line drift out of the
 * column the digits above them line up in.
 */
const numCellSx = {...cellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}

/**
 * The two weight columns are tabular like the numeric ones but LEFT-aligned:
 * each is a stack of three or four lines of differing length, and right-aligning
 * a stack ragged on its left edge makes the column read as though it has come
 * loose from its neighbour. Same call, for the same reason, as the weight
 * corrections table in the sibling Disputes module.
 */
const weightCellSx = {...cellSx, fontVariantNumeric: 'tabular-nums'}

/** The one figure a numeric cell is about. */
const figureSx = {fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.xs, color: NAVY}

/**
 * Its qualifier. Deliberately a caption and NOT a filled Chip: a pill carries
 * as much visual weight as the figure above it, which is what made a count and
 * its coverage read as two unrelated facts rather than one. Colour still
 * carries the tone; size, weight and position say which of the two to read first.
 */
const qualifierSx = (color) => ({
  fontFamily: FONT, fontSize: SIZE['2xs'], lineHeight: 1.5, whiteSpace: 'nowrap', color,
})

/** Dead Wt. / Vol Wt., as narrow labels beside their figures — never broken
 *  across two lines, which would read as two labels rather than one. */
const prefixSx = {
  fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: MUTED,
  whiteSpace: 'nowrap', letterSpacing: '0.04em',
}

/** The side of the declared pair that reaches the chargeable weight. */
const governSx = {fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: NAVY}

/** The freeze is conditional on this material, so it belongs beside the weights
 *  it explains rather than only inside the request drawer. */
const packSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: MUTED, mt: 0.25}

const governTagSx = {fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: INFO, mt: 0.25}

/**
 * The module's own wording for which side governs — `IniRaiseDrawer`'s chip says
 * the same thing as a sentence carrying the figure ("Dead weight governs at
 * 0.75 kg"). Here the figure is already the line above, so the tag drops it.
 */
const GOVERNS_TAG = {
  dead: 'Dead weight governs',
  vol: 'Volumetric weight governs',
  both: 'Dead and volumetric are equal',
}

/**
 * MUI reserves the sort arrow's slot even while the arrow is invisible, so a
 * right-aligned header's label stops ~22px short of the figures beneath it.
 * Reversing the flex direction puts the arrow on the label's left and the label
 * back on the column's own right edge, over the numbers it names.
 */
const rightSortSx = {flexDirection: 'row-reverse'}

/**
 * The module shell (freeze-v2/index.js) sets `& .MuiTableCell-body {px: 1}` for
 * every table under it. That descendant selector outranks `cellSx`'s own
 * `px: 1.5`, so body cells are 8px while `headerCellSx` keeps its 12px — every
 * header sat 4px inside its own column. The shell is not ours to edit, and the
 * header is the side with no competing rule, so it matches the body here.
 */
const headSx = {...headerCellSx, px: 1}

const SUGGESTED_HINT = 'The weight couriers charged most often for this row, read straight off its own '
  + 'charged-weight spread — not a separate estimate.'

const COLUMNS = [
  {key: 'label', label: 'SKU', align: 'left'},
  {key: 'ships', label: 'Shipments', align: 'right'},
  {key: 'dead', label: 'Merchant weight', align: 'left'},
  {key: 'mpw', label: 'Prozo suggested weight', align: 'left', hint: SUGGESTED_HINT},
  // Inherits the `spread` sort that the deleted Variance column used to own —
  // it was the only header that offered it, and it is still the table's default
  // order. These bars ARE that metric drawn: the amber ones are exactly the
  // off-modal shipments, so this is the one surviving column the sort can hang
  // off honestly. `hint` says so out loud because the header noun and the sort
  // key are not the same word.
  {
    key: 'spread',
    label: 'Weight distribution',
    align: 'left',
    hint: 'Sorts by variance: how many of a row’s shipments were charged something other than the suggested weight.',
  },
  // THE ABSORBER, and the reason it is this column. Something has to take every
  // spare pixel or the browser spreads the slack evenly and the measured
  // columns drift apart from each other; parking it on the last column is what
  // also holds the row action against the table's right edge, where an action
  // belongs, and leaves SKU at its own content width instead of trailing a run
  // of dead space between the label and the first figure. Exactly ONE column
  // may carry it — a second would split the slack and reopen both gaps.
  {key: 'actions', label: 'Actions', align: 'right', sortable: false, sx: {width: '100%'}},
]

/**
 * The Merchant weight cell spoken aloud. Its hierarchy — which line is the
 * headline and which are its components — is carried entirely by font size,
 * weight and colour, none of which a screen reader can see, so the whole
 * breakdown is restated in words. Exported so the claim stays assertable in
 * tests.
 */
export function merchantWeightSummary(row, pack) {
  return [
    `Merchant weight ${kg(row.deadKg)}`,
    `dead weight ${kg(row.deadKg)}`,
    `volumetric ${kg(row.volKg)}`,
    pack ? `packed in ${packLabel(pack)}` : 'packing material not on file',
  ].join(', ')
}

/**
 * The selection contract (decided after the plan's own draft was written,
 * on the same hazard Disputes and Catalogue already hit): a filter never
 * silently drops a selected row, but the selection bar must say when some
 * of it is out of view rather than presenting a count that quietly excludes
 * it. `allRows` is the FULL rank-ordered candidate list (never filtered) —
 * `pickedRows` (./rows) resolves the selection against it, so a tick survives
 * the filter that hides its row. `visibleRows` is THIS render's filtered
 * set, every page, not just the current page slice — a row can only be
 * "hidden" by a search/filter narrowing it out, never by pagination.
 *
 * The selection no longer reaches the request drawer: the row action opens it
 * over one row and the page replaces the selection to do it (see `freezeOne`).
 * What this still owns is the strip above the grid — its count, and the
 * warning that part of that count is out of view — which is the only place a
 * merchant learns a filter is hiding rows they ticked. Whether a selection
 * with no bulk action left behind it should exist at all is a product
 * question, raised as a follow-up rather than answered here.
 *
 * Extracted and exported so this derivation is unit-tested without mounting
 * the table — see IniCandidateTable.test.js.
 */
export function selectionDisclosure(allRows, visibleRows, selection) {
  const picked = pickedRows(allRows, selection)
  const visibleKeys = new Set((visibleRows || []).map((r) => r.key))
  return {picked, hidden: picked.filter((r) => !visibleKeys.has(r.key))}
}

/**
 * Weight Freeze (v2) — Initiate: the ranked candidate table.
 *
 * Plain MUI Table (not ProzoDataGrid/DataGrid) — every row here is a
 * multi-line composite (mono label + sub-line, a stacked weight breakdown)
 * plus an inline SVG sparkline, which DataGrid's single-line cell model
 * fights. `disputes-v2/DisputeGrid.js` and this module's own
 * `catalogue/CatalogueTable.js` made the same call for the same reason —
 * this follows their idiom rather than inventing a third.
 *
 * ---- the two weight columns ----
 * A candidate row carries exactly ONE dead/volumetric pair (`deadKg`/`volKg`,
 * the figures `deriveProposal` pre-fills the request drawer from) and ONE
 * observed figure (`mpw`, the mode of the row's own charged-weight
 * histogram). Those are the two independent claims, so they get one column
 * each: Merchant weight is what the account says the product weighs and what
 * it is packed in; Prozo suggested weight is what couriers actually billed
 * most often. The pair is shown once, under Merchant weight, because it is
 * one pair — printing it a second time beside the suggested weight would
 * assert two measurements where the row holds one.
 *
 * Sort and pagination are OWNED HERE, not threaded from the page: the
 * task's own interface list names no `sort`/`page` prop, and `sortRows`/
 * `nextSort` are things this file "consumes" directly, the same way
 * `CatalogueTable.js` keeps its own local `sort` state over rows the page
 * has already filtered. `rows` is that filtered (but not yet sorted) set,
 * in RANK order by default; `allRows` is the full unfiltered list,
 * needed to resolve the selection contract above AND to tell "a filter hid
 * everything" apart from "this merchant has never shipped" — two situations
 * with the same `rows.length === 0` and completely different remedies.
 */
export default function IniCandidateTable({
  rows, allRows, packs, selection, onToggle, onSelectPage, onClear, onClearFilters,
  onFreezeNewSku, onFreezeOne,
}) {
  const [sort, setSort] = React.useState(null)
  const [page, setPage] = React.useState(1)

  // Resets to page 1 on a row-identity change (search, filter) ONLY —
  // `rows` is the memoised filtered array the page hands down, so its
  // reference changes exactly when the underlying filter/search does,
  // never on a re-sort or an unrelated re-render.
  React.useEffect(() => {
    setPage(1)
  }, [rows])

  const sortedRows = React.useMemo(() => sortRows(rows, sort), [rows, sort])

  const packById = React.useMemo(
    () => new Map((packs || []).map((p) => [p.id, p])),
    [packs],
  )

  const {picked, hidden} = React.useMemo(
    () => selectionDisclosure(allRows, rows, selection),
    [allRows, rows, selection],
  )

  const pickedShips = picked.reduce((n, r) => n + (r.ships || 0), 0)
  const pickedDws = picked.reduce((n, r) => n + (r.sorterCount || 0), 0)

  const pages = Math.max(1, Math.ceil(sortedRows.length / PER_PAGE))
  const clampedPage = Math.min(page, pages)
  const pageRows = sortedRows.slice((clampedPage - 1) * PER_PAGE, clampedPage * PER_PAGE)
  const pageKeys = pageRows.map((r) => r.key)
  const allOnPage = pageKeys.length > 0 && pageKeys.every((k) => selection.has(k))
  const someOnPage = pageKeys.some((k) => selection.has(k))

  const handleSort = (key) => setSort((s) => nextSort(s, key))

  return (
    <Box sx={{display: 'grid', gap: GAP.sm}}>
      {/* The explanatory band — the one place that says what the sparkline's
          own colours mean before a reader has to guess from the table alone.
          Withheld when the merchant has no shipments at all: a paragraph about
          the suggested weight and variance describes nothing on screen. */}
      {allRows.length > 0 && (
        <Box sx={{...alertStripSx(INFO), alignItems: 'flex-start'}}>
          <InfoOutlinedIcon sx={{fontSize: 16, mt: '2px'}} />
          <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, lineHeight: 1.6, fontWeight: WEIGHT.regular}}>
            Prozo suggested weight is the charged weight that shows up most often for that row. Variance is the
            share of its shipments charged something else, and the amber bars in the distribution are exactly
            those shipments. High variance is where a freeze is worth raising.
          </Typography>
        </Box>
      )}

      {rows.length === 0 ? (
        <Box sx={dataGridWrapSx}>
          {/* Two different empty tables. With no candidate rows AT ALL there
              is no filter to blame and nothing for "Clear filters" to clear —
              saying so would send a brand-new merchant hunting for a filter
              they never set. Their route out is the same drawer the header
              button opens: a second door, never a second drawer. */}
          {allRows.length === 0 ? (
            <EmptyState
              icon={Inventory2OutlinedIcon}
              title='No shipments on this account yet'
              body='A freeze is normally raised over shipment history, and there is none here yet. Declare the parcel, its weight and its packing material instead.'
              action={<Button size='small' sx={secondaryBtnSx} onClick={onFreezeNewSku}>Freeze weight for a new product</Button>}
            />
          ) : (
            <EmptyState
              icon={SearchOffOutlinedIcon}
              title='Nothing matches that filter'
              body="Clear the search or widen the filter to see the rest of this merchant's shipments."
              action={<Button size='small' sx={secondaryBtnSx} onClick={onClearFilters}>Clear filters</Button>}
            />
          )}
        </Box>
      ) : (
        <Box sx={dataGridWrapSx}>
          {selection.size > 0 && (
            <Box
              sx={{
                ...alertStripSx(PRIMARY_BLUE), justifyContent: 'space-between', flexWrap: 'wrap',
                borderRadius: 0, borderLeft: 'none', borderRight: 'none', borderTop: 'none',
              }}
            >
              <Box sx={{minWidth: 0}}>
                <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold}}>
                  {int(picked.length)} {picked.length === 1 ? 'SKU' : 'SKUs'} selected
                  {hidden.length > 0 && (
                    <Tooltip title={`Hidden: ${hidden.map((r) => r.label).join(', ')}`}>
                      <Box component='span' tabIndex={0} sx={{fontWeight: WEIGHT.regular, cursor: 'help'}}>
                        {' '}({int(hidden.length)} not visible under current filters)
                      </Box>
                    </Tooltip>
                  )}
                </Typography>
                <Typography sx={subtleTextSx}>
                  {int(pickedShips)} shipments, {int(pickedDws)} with DWS images
                </Typography>
              </Box>
              <Box sx={{display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap'}}>
                {hidden.length > 0 && (
                  <Button
                    size='small'
                    onClick={onClearFilters}
                    sx={{fontFamily: FONT, fontSize: SIZE['2xs'], textTransform: 'none', color: PRIMARY_BLUE}}
                  >
                    Clear filters to show them
                  </Button>
                )}
                <Button
                  size='small'
                  onClick={onClear}
                  sx={{fontFamily: FONT, fontSize: SIZE['2xs'], textTransform: 'none', color: SUBTLE}}
                >
                  Clear selection
                </Button>
              </Box>
            </Box>
          )}

          <Table sx={{minWidth: 1000}}>
            <TableHead>
              <TableRow>
                <TableCell sx={headSx} padding='checkbox'>
                  <Checkbox
                    size='small'
                    checked={allOnPage}
                    indeterminate={someOnPage && !allOnPage}
                    onChange={() => onSelectPage(pageKeys, !allOnPage)}
                    inputProps={{'aria-label': 'Select all rows on this page'}}
                  />
                </TableCell>
                {COLUMNS.map((col) => {
                  // A column of buttons has no order to put them in, so the
                  // Actions header is the label alone. Every other header is a
                  // sort control, and offering a dead one here would promise an
                  // ordering the column cannot deliver.
                  const control = col.sortable === false ? col.label : (
                    <TableSortLabel
                      active={!!sort && sort.key === col.key}
                      direction={sort && sort.key === col.key ? sort.dir : 'desc'}
                      onClick={() => handleSort(col.key)}
                      sx={col.align === 'right' ? rightSortSx : undefined}
                    >
                      {col.label}
                    </TableSortLabel>
                  )
                  return (
                    <TableCell key={col.key} sx={{...headSx, textAlign: col.align, ...col.sx}}>
                      {col.hint ? <Tooltip title={col.hint}>{control}</Tooltip> : control}
                    </TableCell>
                  )
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((row) => {
                const facts = modalFacts(row.hist, row.mpw)
                const pack = packById.get(row.packId) || null
                const governs = weightFacts(row.deadKg, row.volKg, row.mpw).governs

                return (
                  <TableRow
                    key={row.key}
                    hover
                    selected={selection.has(row.key)}
                    onClick={(event) => {
                      // Never toggle twice for a click that started on the
                      // row's own checkbox (or any future interactive cell) —
                      // that control already handles its own click.
                      if (event.target.closest('button, input, a, [role="button"]')) return
                      onToggle(row.key)
                    }}
                    sx={{cursor: 'pointer'}}
                  >
                    <TableCell sx={cellSx} padding='checkbox'>
                      <Checkbox
                        size='small'
                        checked={selection.has(row.key)}
                        onChange={() => onToggle(row.key)}
                        inputProps={{'aria-label': 'Select ' + row.label}}
                      />
                    </TableCell>

                    <TableCell sx={cellSx}>
                      <Typography
                        title={row.label}
                        sx={{
                          fontFamily: 'monospace', fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY,
                          maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {row.label}
                      </Typography>
                      <Typography
                        title={row.sub}
                        sx={{...subtleTextSx, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
                      >
                        {row.sub}
                      </Typography>
                    </TableCell>

                    <TableCell sx={numCellSx}>
                      <Typography sx={figureSx}>{int(row.ships)}</Typography>
                    </TableCell>

                    {/* Dead is the headline as well as the first line of the
                        pair: the weight the account declares for a product IS
                        its dead weight — the volumetric belongs to the packing
                        material named underneath — so a single source cannot
                        disagree with itself. */}
                    <TableCell sx={weightCellSx} aria-label={merchantWeightSummary(row, pack)}>
                      <Typography sx={figureSx}>{kg(row.deadKg)}</Typography>
                      <Box sx={{
                        display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 0.75,
                        justifyContent: 'start', alignItems: 'baseline', mt: 0.25,
                      }}>
                        <Typography sx={prefixSx}>Dead Wt.</Typography>
                        <Typography sx={governs === 'vol' ? subtleTextSx : governSx}>{kg(row.deadKg)}</Typography>
                        <Typography sx={prefixSx}>Vol Wt.</Typography>
                        <Typography sx={governs === 'dead' ? subtleTextSx : governSx}>{kg(row.volKg)}</Typography>
                      </Box>
                      <Typography sx={packSx}>
                        {pack ? packLabel(pack) : 'Packing material not on file'}
                      </Typography>
                    </TableCell>

                    <TableCell sx={weightCellSx}>
                      <Typography sx={{...figureSx, fontWeight: WEIGHT.bold}}>{kg(row.mpw)}</Typography>
                      <Typography sx={governTagSx}>{GOVERNS_TAG[governs]}</Typography>
                    </TableCell>

                    {/* The off-modal count is the caption the bars never had — it
                        names exactly what the amber ones are, and it is the figure
                        the `spread` sort on this header orders by. */}
                    <TableCell sx={{...cellSx, fontVariantNumeric: 'tabular-nums'}}>
                      <IniSpark hist={row.hist} mpw={row.mpw} />
                      {/* No bands means no charged weights on record at all, and
                          IniSpark has already said "No data" — claiming "One
                          weight only" underneath it would assert an observation
                          this row does not have. */}
                      {facts.bands > 0 && (
                        <Typography sx={qualifierSx(SUBTLE)}>
                          {facts.bands === 1 ? 'One weight only' : int(facts.off) + ' of ' + int(facts.total) + ' off modal'}
                        </Typography>
                      )}
                    </TableCell>

                    {/* The one and only route into the request drawer from this
                        table — it replaces the selection with this row rather
                        than raising a second request path (see the page's
                        `freezeOne`). Contained/primary because it is the same
                        act as the page's own "Freeze weight for a new product"
                        — raise a freeze, at a different scope — and the
                        merchant asked for the two to read alike: blue ground,
                        white text. It is NOT the only primary on screen, and
                        claiming otherwise would be the wrong reason to keep it:
                        one of these renders per row, so a full page paints ten
                        beside that single page-level button. Held to the grid's
                        own caption size so ten of them do not shout it down.
                        `col.align` reaches the header only, so the body cell
                        states its own alignment.

                        `stopPropagation` because this cell holds the table's
                        slack: most of its width is empty space to the LEFT of
                        the button, which makes it the largest hit area in the
                        row. The row handler's `closest` guard only spares the
                        button itself, so a click that undershoots would
                        silently tick the checkbox — a state change with no
                        visible cause, under a header that promises an action.
                        The Button's own handler has already run by the time
                        this fires; only the bubble is stopped. */}
                    <TableCell
                      sx={{...cellSx, textAlign: 'right'}}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Button
                        size='small'
                        variant='contained'
                        startIcon={<AcUnitOutlinedIcon sx={{fontSize: 15}} />}
                        sx={{...primaryBtnSx, fontSize: SIZE['2xs'], py: 0.5}}
                        // Ten of these render per page and the visible words
                        // are the same on all of them. Named like the module's
                        // other row-scoped actions (DwsThumb, DisputeGrid) so
                        // they are ten distinct controls to a screen reader —
                        // and the visible label stays inside the accessible
                        // name, which speech input needs.
                        aria-label={'Freeze SKU ' + row.label}
                        onClick={() => onFreezeOne(row.key)}
                      >
                        Freeze SKU
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>

          {pages > 1 && (
            <Box sx={{display: 'flex', justifyContent: 'flex-end', p: 1.5}}>
              <Pagination
                size='small'
                page={clampedPage}
                count={pages}
                onChange={(event, p) => setPage(p)}
                sx={{'& .MuiPaginationItem-root': {fontFamily: FONT, fontSize: SIZE['2xs']}}}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}
